import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Lottie } from 'lottie-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PracticeExercise from '../components/PracticeExercise';
import Bilingual, { bilingualOverlayCode } from '../components/Bilingual';
import { PRACTICE_LESSONS } from '../data/practice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sprint 10.8 — Amma Help language labels shown next to the 🔊 buttons.
// Each label uses the native script so a parent scanning the page recognises
// their language instantly. English content DISPLAY is untouched — this
// controls audio playback only.
const LANG_LABEL = {
  si: 'සිංහල',
  ta: 'தமிழ்',
  hi: 'हिन्दी',
  ms: 'B. Melayu',
  bn: 'বাংলা',
};

export default function Quiz() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { lesson_id } = useParams();
  const regId = useMemo(() => {
    try { return localStorage.getItem('gop_registration_id'); } catch (e) { return null; }
  }, []);
  const [lesson, setLesson] = useState(null);
  const [audio, setAudio] = useState({});     // Sprint 9 — { field: url } (English)
  // Sprint 10.8 — Amma Help: non-English scaffolding audio (display stays English)
  const [ammaAudio, setAmmaAudio] = useState({}); // { lang: { field: url } }
  const [ammaLangs, setAmmaLangs] = useState([]); // sorted list of available codes
  const [ammaLang, setAmmaLang] = useState(null); // null = off; else 'si'|'ta'|'hi'|'ms'|'bn'
  const [playingKey, setPlayingKey] = useState(null);
  const audioElRef = useRef(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startedAt = useRef(Date.now());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!lesson_id) return;
    // Sprint 10.5 — lesson content is English-only by policy. `?lang=en`
    // is explicit; audio endpoint honours the same policy server-side.
    axios.get(`${API}/quiz/lesson/${lesson_id}?lang=en`)
      .then(r => setLesson(r.data))
      .catch(() => setLesson({ error: true }));
  }, [lesson_id]);

  // Sprint 9 — pull the audio manifest slice for this lesson (en-only under Sprint 10.5).
  useEffect(() => {
    if (!lesson_id) return;
    axios.get(`${API}/audio/lesson/${lesson_id}?lang=en`)
      .then(r => setAudio(r.data.audio || {}))
      .catch(() => setAudio({}));
  }, [lesson_id]);

  // Sprint 10.8 — pull Amma Help audio (non-English scaffolding). Silent on 404.
  useEffect(() => {
    if (!lesson_id) return;
    axios.get(`${API}/audio/lesson/${lesson_id}/amma-help`)
      .then(r => {
        setAmmaAudio(r.data.audio_by_lang || {});
        setAmmaLangs(r.data.languages || []);
      })
      .catch(() => { setAmmaAudio({}); setAmmaLangs([]); });
  }, [lesson_id]);

  const playAudio = (field) => {
    // Sprint 10.8 — prefer Amma Help audio (non-English) when toggled on;
    // fall back silently to English if the Amma track for this field
    // isn't generated yet. English display copy is never swapped.
    const ammaRel = ammaLang ? (ammaAudio[ammaLang] || {})[field] : null;
    const rel = ammaRel || audio[field];
    if (!rel) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}${rel}`;
    if (audioElRef.current) {
      audioElRef.current.pause();
    }
    const el = new Audio(url);
    audioElRef.current = el;
    setPlayingKey(field);
    el.addEventListener('ended', () => setPlayingKey(null));
    el.addEventListener('error', () => setPlayingKey(null));
    el.play().catch(() => setPlayingKey(null));
  };

  const stopAudio = () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    setPlayingKey(null);
  };

  const PlayBtn = ({ field, size = 'sm', testId }) => {
    const active = playingKey === field;
    const hasAmma = ammaLang && (ammaAudio[ammaLang] || {})[field];
    const disabled = !audio[field] && !hasAmma;
    const ammaOnly = !audio[field] && hasAmma;
    return (
      <button
        onClick={() => (active ? stopAudio() : playAudio(field))}
        disabled={disabled}
        title={
          disabled ? 'Audio not generated yet' :
          active ? 'Stop' :
          ammaLang ? `Play in ${LANG_LABEL[ammaLang] || ammaLang}` : 'Play'
        }
        data-testid={testId}
        className={`inline-flex items-center gap-1 rounded-full border ${
          size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'
        } ${
          disabled ? 'opacity-30 cursor-not-allowed' :
          active ? 'bg-emerald-100 border-emerald-400 text-emerald-900' :
          ammaLang ? 'bg-violet-50 border-violet-300 hover:bg-violet-100 text-violet-900' :
          'bg-white hover:bg-slate-50'
        }`}
      >
        <span>{active ? '⏸' : '🔊'}</span>
        {size === 'lg' && (
          <span>
            {active ? 'Playing…' : (ammaLang ? `Listen · ${LANG_LABEL[ammaLang] || ammaLang}` : 'Listen')}
            {ammaOnly && <sup className="ml-0.5 text-[9px] opacity-70">Amma</sup>}
          </span>
        )}
      </button>
    );
  };

  if (!regId) return <Navigate to="/register" replace />;
  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-sm text-muted-foreground" data-testid="quiz-loading">
        <div className="text-center space-y-2">
          <div style={{ width: 120, height: 120 }} className="mx-auto">
            <Lottie src="/lottie/book.json" autoplay loop />
          </div>
          <p>{t('common.loading') || 'Loading lesson…'}</p>
        </div>
      </div>
    );
  }
  if (lesson.error) return <div className="p-8 text-red-600">{t('quiz.not_found') || 'Lesson not found.'}</div>;

  // Sprint 10.6.7 — bilingual overlay for si/ta locales. English subject
  // lessons stay English-only regardless of locale (the subject IS English).
  const rawOverlayCode = bilingualOverlayCode(i18n.language);
  const overlayCode = (lesson.subject === 'english') ? null : rawOverlayCode;
  const tx = overlayCode ? (lesson.translations?.[overlayCode] || null) : null;
  const txQuestions = tx?.questions || [];
  const view = { title: lesson.title, questions: lesson.questions };

  const submit = async () => {
    setSubmitting(true);
    const timeSpent = Math.round((Date.now() - startedAt.current) / 1000);
    try {
      const answersArr = lesson.questions.map(q => ({
        question_id: q.question_id,
        selected_option: answers[q.question_id] || '',
      }));
      const { data } = await axios.post(`${API}/quiz/submit`, {
        registration_id: regId,
        lesson_id,
        answers: answersArr,
        time_spent_seconds: timeSpent,
      });
      setResult(data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const isPerfect = result.score === 100;
    const isPass = result.score >= 60;
    return (
      <div className="min-h-screen bg-background px-4 py-8 relative overflow-hidden" data-testid="quiz-results-page">
        {/* Confetti overlay (Lottie) — only on pass */}
        {isPass && (
          <div className="pointer-events-none absolute inset-0 z-0 opacity-70" aria-hidden data-testid="results-confetti">
            <Lottie src="/lottie/confetti.json" autoplay loop={false} style={{ height: '100%', width: '100%' }} />
          </div>
        )}
        <div className="max-w-2xl mx-auto space-y-6 relative z-10">
          <Card className="card-shadow gop-fade-in-up" data-testid="quiz-score-card">
            <CardHeader className="text-center">
              {/* Trophy/success/thumbs badge */}
              <div className="mx-auto mb-1" style={{ width: 140, height: 140 }} data-testid="results-lottie-badge">
                {isPerfect ? (
                  <Lottie src="/lottie/trophy.json" autoplay loop />
                ) : isPass ? (
                  <Lottie src="/lottie/success.json" autoplay loop={false} />
                ) : (
                  <div className="text-7xl leading-none">💪</div>
                )}
              </div>
              <CardTitle className="text-4xl">
                {result.score}%
              </CardTitle>
              <CardDescription>
                {t('quiz.result.line', {
                  correct: result.correct_count,
                  total: result.total_count,
                }) || `${result.correct_count} of ${result.total_count} correct`}
              </CardDescription>
            </CardHeader>
            {result.badges_earned?.length > 0 && (
              <CardContent className="text-center" data-testid="badge-popup">
                <p className="text-sm mb-2">{t('quiz.badges.earned') || 'New badges earned:'}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {result.badges_earned.map(b => (
                    <Badge key={b} className="text-base py-1 px-3 gop-fade-in-up">🏅 {b}</Badge>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <div className="space-y-3">
            {lesson.questions.map((q, i) => {
              const fb = result.correct_answers.find(f => f.question_id === q.question_id) || {};
              const yourLetter = fb.selected_option;
              const correctLetter = fb.correct_option;
              return (
                <Card key={q.question_id} data-testid={`feedback-${q.question_id}`}
                      className={fb.correct ? 'border-emerald-500' : 'border-red-500'}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-start justify-between gap-3">
                      <span>{i + 1}. {q.q}</span>
                      <span>{fb.correct ? '✓' : '✗'}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {Object.entries(q.options).map(([k, v]) => {
                      const isCorrect = k === correctLetter;
                      const isYours = k === yourLetter;
                      return (
                        <div key={k} className={`px-2 py-1 rounded ${
                          isCorrect ? 'bg-emerald-100 text-emerald-900' :
                          isYours ? 'bg-red-100 text-red-900' : ''
                        }`}>
                          <strong>{k}.</strong> {v} {isCorrect && '← ' + (t('quiz.correct') || 'correct')}
                          {isYours && !isCorrect && ` ← ${t('quiz.your_answer') || 'your answer'}`}
                        </div>
                      );
                    })}
                    {fb.explanation && (
                      <p className="text-xs text-muted-foreground pt-2 italic">
                        {t('quiz.explanation') || 'Why:'} {fb.explanation}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex gap-3">
            <Button className="flex-1" onClick={() => navigate('/lessons')} data-testid="back-to-lessons-btn">
              {t('quiz.back') || 'Back to lessons'}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => navigate(`/progress/${regId}`)} data-testid="see-progress-btn">
              {t('quiz.see_progress') || 'See my progress →'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const secs = Math.floor((now - startedAt.current) / 1000);
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const allAnswered = view.questions.every(q => answers[q.question_id]);

  return (
    <div className="min-h-screen bg-background px-4 py-8" data-testid="quiz-page">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Task T — lesson hero image */}
        {lesson.lesson_image_url && (
          <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[16/9] gop-fade-in-up" data-testid="quiz-hero-image-wrap">
            <img
              src={lesson.lesson_image_url}
              alt={lesson.title}
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid="quiz-hero-image"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
          </div>
        )}

        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
             data-testid="en-only-content-notice">
          {tx ? (
            overlayCode === 'si'
              ? 'ද්විභාෂා පාඩම: සිංහල උඩ · English below · Audio stays English'
              : 'இருமொழி பாடம்: தமிழ் மேலே · English below · Audio stays English'
          ) : (lesson.subject === 'english'
              ? (t('lessons.english_only_notice') || 'English lessons — taught in English.')
              : (t('lessons.en_only_notice') || 'Lessons are delivered in English — your progress and support are in your language.'))}
        </div>

        {/* Sprint 10.8 — Amma Help: switch narration to a non-English language
            for a parent scaffolding a child, without changing the English text. */}
        {ammaLangs.length > 0 && (
          <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 flex flex-wrap items-center gap-2"
               data-testid="amma-help-bar">
            <span className="text-sm text-violet-900 font-semibold mr-1">🌐 Amma Help</span>
            <span className="text-xs text-violet-700 hidden sm:inline mr-2">
              Play narration in your language (text stays English)
            </span>
            <button
              type="button"
              onClick={() => setAmmaLang(null)}
              data-testid="amma-lang-off"
              className={`px-2.5 py-1 rounded-full text-xs border ${
                ammaLang === null
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white border-violet-200 text-violet-800 hover:bg-violet-100'
              }`}
            >
              Off
            </button>
            {ammaLangs.map(code => (
              <button
                key={code}
                type="button"
                onClick={() => setAmmaLang(code)}
                data-testid={`amma-lang-${code}`}
                className={`px-2.5 py-1 rounded-full text-xs border ${
                  ammaLang === code
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white border-violet-200 text-violet-800 hover:bg-violet-100'
                }`}
              >
                {LANG_LABEL[code] || code}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{lesson.subject}</p>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="quiz-title">
              <Bilingual
                local={tx?.title}
                english={view.title}
                className="text-2xl font-bold"
                localAs="span"
                englishAs="span"
                testId="quiz-title-body"
              />
              <PlayBtn field="title" size="lg" testId="audio-play-title" />
            </h1>
            {audio.body_hook && (
              <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
                <PlayBtn field="body_hook" testId="audio-play-body-hook" />
                <span>Play lesson intro</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-mono text-lg" data-testid="quiz-timer">⏱ {mm}:{ss}</div>
            <p className="text-xs text-muted-foreground">
              {t('quiz.progress', { done: Object.keys(answers).length, total: view.questions.length }) ||
                `${Object.keys(answers).length} / ${view.questions.length}`}
            </p>
          </div>
        </div>

        {/* Task T — Interactive Practice (POC for 3 lessons) */}
        {PRACTICE_LESSONS[lesson_id] && (
          <div data-testid="practice-section" className="gop-fade-in-up">
            <PracticeExercise config={PRACTICE_LESSONS[lesson_id]} />
          </div>
        )}

        {view.questions.map((q, i) => {
          const txQ = txQuestions[i] || null;
          return (
          <Card key={q.question_id} data-testid={`quiz-question-${q.question_id}`}>
            <CardHeader>
              <CardTitle className="text-base flex items-start justify-between gap-2">
                <span className="flex-1">
                  <span className="font-semibold mr-2">{i + 1}.</span>
                  <Bilingual
                    local={txQ?.q}
                    english={q.q}
                    className="font-semibold"
                    localAs="span"
                    englishAs="span"
                    testId={`quiz-q-${i + 1}-text`}
                  />
                </span>
                <PlayBtn field={`question_${String(i + 1).padStart(2, '0')}`}
                         testId={`audio-play-q-${i + 1}`} />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(q.options).map(([k, v]) => {
                const txOpt = (txQ?.options || {})[k];
                return (
                <label key={k}
                       className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer ${
                         answers[q.question_id] === k ? 'border-primary bg-primary/5' : ''
                       }`}
                       data-testid={`option-${q.question_id}-${k}`}>
                  <input type="radio"
                         name={q.question_id}
                         value={k}
                         checked={answers[q.question_id] === k}
                         onChange={() => setAnswers(a => ({ ...a, [q.question_id]: k }))} />
                  <span className="flex-1">
                    <strong className="mr-1">{k}.</strong>
                    <Bilingual
                      local={txOpt}
                      english={v}
                      className=""
                      localAs="span"
                      englishAs="span"
                      testId={`option-${q.question_id}-${k}-text`}
                    />
                  </span>
                </label>
                );
              })}
            </CardContent>
          </Card>
          );
        })}

        <Button className="w-full" size="lg" disabled={!allAnswered || submitting}
                onClick={submit} data-testid="submit-quiz-btn">
          {submitting ? (t('quiz.submitting') || 'Submitting…') : (t('quiz.submit') || 'Submit quiz')}
        </Button>
      </div>
    </div>
  );
}
